// src/components/BenefitsComponent.js

import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, useAnimation } from "framer-motion";

const BenefitsComponent = ({ data }) => {
    const controls = useAnimation();
    const columnRef = useRef(null);
    const items = useMemo(() => data?.acf?.benefit_items || [], [data]);

    const [isMobile, setIsMobile] = useState(false);
    const [mobileMetrics, setMobileMetrics] = useState({
        containerHeight: 0,
        scrollDistance: 0,
    });

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 767);
        };

        handleResize();
        window.addEventListener("resize", handleResize);

        return () => window.removeEventListener("resize", handleResize);
    }, []);

    // Desktop (vw based)
    const ITEM_HEIGHT_VW = 6.5;
    const VISIBLE_COUNT = 3.2;
    const SCROLL_SPEED = 2;
    const SCROLL_SPEED_MOBILE = 40; // px per second

    useEffect(() => {
        if (!isMobile || !columnRef.current || items.length === 0) {
            setMobileMetrics((prev) => {
                if (prev.containerHeight === 0 && prev.scrollDistance === 0) {
                    return prev;
                }

                return {
                    containerHeight: 0,
                    scrollDistance: 0,
                };
            });
            return;
        }

        const measure = () => {
            if (!columnRef.current) return;

            const cards = Array.from(columnRef.current.children).slice(0, items.length);
            const heights = cards.map((card) => card.getBoundingClientRect().height);

            if (heights.length === 0) return;

            const wholeCards = Math.floor(VISIBLE_COUNT);
            const partialCard = VISIBLE_COUNT - wholeCards;

            const visibleHeight = heights
                .slice(0, wholeCards)
                .reduce((sum, height) => sum + height, 0);

            const partialHeight = (heights[wholeCards] || 0) * partialCard;
            const totalHeight = heights.reduce((sum, height) => sum + height, 0);

            setMobileMetrics({
                containerHeight: visibleHeight + partialHeight,
                scrollDistance: totalHeight,
            });
        };

        const frameId = requestAnimationFrame(measure);
        window.addEventListener("resize", measure);

        return () => {
            cancelAnimationFrame(frameId);
            window.removeEventListener("resize", measure);
        };
    }, [items, isMobile]);

    const scrollDistance = isMobile
        ? mobileMetrics.scrollDistance
        : items.length * ITEM_HEIGHT_VW;

    const duration = isMobile
        ? (scrollDistance > 0 ? scrollDistance / SCROLL_SPEED_MOBILE : 0)
        : scrollDistance / SCROLL_SPEED;

    useEffect(() => {
        if (items.length === 0 || scrollDistance <= 0 || duration <= 0) {
            controls.stop();
            return;
        }

        controls.start({
            y: isMobile
                ? ["0px", `-${scrollDistance}px`]
                : ["0vw", `-${scrollDistance}vw`],
            transition: {
                duration,
                ease: "linear",
                repeat: Infinity,
                repeatType: "loop",
            },
        });
    }, [controls, duration, isMobile, items.length, scrollDistance]);

    return (
        <div className="comm_inner d_flex flex_column no_gap">
            <div className="stack_titlebar d_flex flex_column align_center">
                <div className="benefits_topbar stack_top_head d_flex justify_center align_center">
                    <span
                        className="top_icon line_none"
                        dangerouslySetInnerHTML={{ __html: data?.acf?.benefits_top_icon }}
                    />
                    <p className="benefits_title stack_top_para mb_0">
                        {data?.acf?.benefits_top_title}
                    </p>
                </div>
            </div>

            <div
                className="benefits_slider_container"
                style={{
                    height: isMobile
                        ? (mobileMetrics.containerHeight > 0
                            ? `${mobileMetrics.containerHeight}px`
                            : "auto")
                        : `${VISIBLE_COUNT * ITEM_HEIGHT_VW}vw`,
                    overflow: "hidden",
                }}
            >
                <motion.div
                    ref={columnRef}
                    className="benefits_column"
                    animate={controls}
                    style={{ display: "flex", flexDirection: "column" }}
                >
                    {[...items, ...items].map((item, index) => (
                        <div
                            key={index}
                            className="benefit_card d_flex align_center"
                            style={{
                                height: isMobile ? "auto" : `${ITEM_HEIGHT_VW}vw`,
                                flexShrink: 0,
                            }}
                        >
                            <span
                                className="benefit_icon d_inline_flex comm_btn"
                                dangerouslySetInnerHTML={{ __html: item.benefit_icon }}
                            />
                            <div className="dot-box d_flex align_center p_0">
                                <div className="dot_container d_flex align_start justify_center">
                                    <div className="dot"></div>
                                </div>
                            </div>
                            <div className="d_flex benifit_data_box align_center">
                                <div className="benefit_percentage d_inline_flex align_center">
                                    <span className="benefit_percentage_data">
                                        {item.benefit_percentage}
                                    </span>
                                    <h5 className="benefit_percentage_sign">%</h5>
                                </div>
                                <div className="benefit_text">
                                    {item.benefit_text}
                                </div>
                            </div>
                        </div>
                    ))}
                </motion.div>
            </div>
        </div>
    );
};

export default BenefitsComponent;
